import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Vector2, ShaderMaterial } from 'three';

const FogShaderMaterial = {
    uniforms: {
        uTime: { value: 0 },
        uResolution: { value: new Vector2() },
    },
    vertexShader: `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
    fragmentShader: `
    uniform float uTime;
    varying vec2 vUv;

    // Simplex 2D noise
    vec3 permute(vec3 x) { return mod(((x*34.0)+1.0)*x, 289.0); }

    float snoise(vec2 v){
      const vec4 C = vec4(0.211324865405187, 0.366025403784439,
               -0.577350269189626, 0.024390243902439);
      vec2 i  = floor(v + dot(v, C.yy) );
      vec2 x0 = v -   i + dot(i, C.xx);
      vec2 i1;
      i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
      vec4 x12 = x0.xyxy + C.xxzz;
      x12.xy -= i1;
      i = mod(i, 289.0);
      vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
      + i.x + vec3(0.0, i1.x, 1.0 ));
      vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
      m = m*m ;
      m = m*m ;
      vec3 x = 2.0 * fract(p * C.www) - 1.0;
      vec3 h = abs(x) - 0.5;
      vec3 ox = floor(x + 0.5);
      vec3 a0 = x - ox;
      m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
      vec3 g;
      g.x  = a0.x  * x0.x  + h.x  * x0.y;
      g.yz = a0.yz * x12.xz + h.yz * x12.yw;
      return 130.0 * dot(m, g);
    }

    void main() {
      vec2 uv = vUv;
      float time = uTime;

      // Layer 1: Bottom-Right to Top-Left Drift
      // Primary volume entering from the corner
      // x: positive offset moves pattern left, y: negative offset moves pattern up (w/ standard GL coords)
      vec2 flow1 = vec2(time * 0.06, time * -0.04); 
      float noise1 = snoise(uv * 2.5 + flow1);
      
      // Layer 2: Opposing Cross Flow
      // slower, moving more purely Left-to-Right to catch the "leaving left middle" feel
      vec2 flow2 = vec2(time * 0.04, time * 0.01);
      float noise2 = snoise(uv * 5.0 + flow2 + vec2(43.0, 12.0)); // Offset to de-sync
      
      // Layer 3: Vertical/Chaotic Interference
      // Moves slowly downwards to create "swirling" effect against the upward drift of Layer 1
      vec2 flow3 = vec2(sin(time * 0.3) * 0.03, time * 0.03); 
      float noise3 = snoise(uv * 8.0 + flow3);

      // Noise Combination
      // Weighted to allow the diagonal drift (Layer 1) to dominate but be broken up by Layer 2
      float fog = noise1 * 0.55 + noise2 * 0.35 + noise3 * 0.15;
      
      // Density adjustments
      // Keeping it voluminous
      fog = smoothstep(0.0, 0.85, fog + 0.5); 

      // Color Mixing
      vec3 fogColor = vec3(0.92, 0.92, 0.95);
      
      // Alpha
      float alpha = fog * 0.25; 
      
      gl_FragColor = vec4(fogColor, alpha);
    }
  `
};

const FogPlane: React.FC = () => {
    const meshRef = useRef<any>(null);

    const material = useMemo(() => new ShaderMaterial({
        uniforms: {
            uTime: { value: 0.0 },
            uResolution: { value: new Vector2(window.innerWidth, window.innerHeight) }
        },
        vertexShader: FogShaderMaterial.vertexShader,
        fragmentShader: FogShaderMaterial.fragmentShader,
        transparent: true,
        depthWrite: false,
    }), []);

    useFrame((state) => {
        if (meshRef.current) {
            meshRef.current.material.uniforms.uTime.value = state.clock.getElapsedTime();
        }
    });

    return (
        <mesh ref={meshRef}>
            <planeGeometry args={[25, 20]} />
            <primitive object={material} attach="material" />
        </mesh>
    );
};

const FogBackground: React.FC = () => {
    return (
        <div className="absolute inset-0 z-0 pointer-events-none grayscale opacity-100 md:opacity-70 mix-blend-screen">
            <Canvas
                camera={{ position: [0, 0, 5], fov: 75 }}
                style={{ width: '100%', height: '100%' }}
                gl={{ alpha: true }}
            >
                <FogPlane />
            </Canvas>
        </div>
    );
};

export default FogBackground;
